import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    UserList, Handshake, Briefcase, MagnifyingGlass, X,
    Clock, ArrowRight, Globe, Flame, ChartLine, Gift,
    CaretDown, ListBullets, GridFour,
} from "@phosphor-icons/react";
import { SLAPill } from "@/components/admin/SLAPill";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import BackToBreadcrumb from "@/components/admin/BackToBreadcrumb";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";

/* ============================================================================
   AdminLeadsHub — unified inbox for every kind of lead
   ----------------------------------------------------------------------------
   Tabs: All | Web | B2B | Applications | Referrals
   Each row shows the SLA pill so admin can spot breaches immediately.
   Clicking a row deep-links to /admin/leads/:kind/:id for the unified detail
   page (status flow + audit timeline + mandatory-comment actions).

   iter100.228 — Referrals tab embeds the AdminReferrals command center
   (doctor-referral demo-link pipeline) so admins have ONE surface for every
   lead source: web forms + B2B outreach + provider applications + doctor
   referrals. Legacy /admin/referrals redirects here (see App.js).
   ========================================================================== */

/* iter100.250 — Referrals tab removed. It embedded a full standalone
   sub-app (AdminReferrals: its own header + KPIs + filter pills + list)
   which clashed with the LeadsHub grammar and looked like a "page inside
   a page". Restored /admin/referrals as its own sidebar item. If admins
   want to jump there from here, use the header "See referrals →" link. */
const TABS = [
    { id: "all",         label: "All",          Icon: UserList },
    { id: "web",         label: "Web",          Icon: Globe },
    { id: "b2b",         label: "B2B",          Icon: Handshake },
    { id: "application", label: "Applications", Icon: Briefcase },
];

const KIND_PILLS = {
    web: { label: "Web", cls: "bg-sky-100 text-sky-700 border-sky-200", Icon: Globe },
    b2b: { label: "B2B", cls: "bg-violet-100 text-violet-700 border-violet-200", Icon: Handshake },
    application: { label: "App", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Briefcase },
};

const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export default function AdminLeadsHub() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab");
    const [tab, setTab] = useState(TABS.some((t) => t.id === initialTab) ? initialTab : "all");
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState("");
    const [slaFilter, setSlaFilter] = useState("all"); // all | breached | at_risk | within | met | not_assigned
    const [statusFilter, setStatusFilter] = useState("open"); // open | terminal | all
    /* iter100.251 — Match the doctor's My Patients toolbar. Sort +
       Assignee filter + List/Grid view toggle in the search bar's
       right slot. Persisted in localStorage so a returning admin
       lands on the same layout. */
    const [sort, setSort] = useState(""); // "" | newest | oldest | breached | name
    const [assignee, setAssignee] = useState("all"); // all | unassigned | <userId>
    const [view, setView] = useState(() => {
        try { return localStorage.getItem("leads_hub_view") || "list"; } catch { return "list"; }
    });
    const setViewPersisted = (v) => {
        setView(v);
        try { localStorage.setItem("leads_hub_view", v); } catch { /* ignore */ }
    };
    const [loading, setLoading] = useState(true);

    // Keep the URL ?tab= synced as the user clicks tabs (deep-linkable + back-button friendly)
    useEffect(() => {
        const current = searchParams.get("tab");
        if (tab === "all") {
            if (current) { searchParams.delete("tab"); setSearchParams(searchParams, { replace: true }); }
        } else if (current !== tab) {
            searchParams.set("tab", tab);
            setSearchParams(searchParams, { replace: true });
        }
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = () => {
        setLoading(true);
        const kindParam = tab === "all" ? "" : `kind=${tab}`;
        Promise.all([
            api.get(`/admin/leads-hub${kindParam ? `?${kindParam}` : ""}`),
            api.get("/admin/leads-hub/stats"),
        ]).then(([list, s]) => {
            setItems(list.data || []);
            setStats(s.data || null);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const TERMINAL_STATUSES = useMemo(() => new Set([
        "converted", "not_interested", "invalid",
        "won", "lost",
        "approved", "rejected",
    ]), []);

    const filtered = useMemo(() => {
        let list = items;
        if (slaFilter !== "all") list = list.filter((r) => r.sla?.state === slaFilter);
        if (statusFilter === "open") list = list.filter((r) => !TERMINAL_STATUSES.has(r.status));
        else if (statusFilter === "terminal") list = list.filter((r) => TERMINAL_STATUSES.has(r.status));
        /* iter100.251 — Assignee dropdown */
        if (assignee === "unassigned") list = list.filter((r) => !r.assignee_id);
        else if (assignee !== "all") list = list.filter((r) => r.assignee_id === assignee);
        if (q.trim()) {
            const ql = q.toLowerCase();
            list = list.filter((r) =>
                [r.title, r.subtitle, r.contact_name, r.email, r.phone, r.city, r.clinic]
                    .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql))
            );
        }
        /* iter100.251 — Sort ordering */
        const SLA_STATE_ORDER = { breached: 0, at_risk: 1, within: 2, met: 3, not_assigned: 4 };
        if (sort === "oldest") {
            list = [...list].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        } else if (sort === "breached") {
            list = [...list].sort((a, b) => {
                const aa = SLA_STATE_ORDER[a.sla?.state] ?? 99;
                const bb = SLA_STATE_ORDER[b.sla?.state] ?? 99;
                if (aa !== bb) return aa - bb;
                return (b.created_at || "").localeCompare(a.created_at || "");
            });
        } else if (sort === "name") {
            list = [...list].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        } else {
            // Default: newest first
            list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        }
        return list;
    }, [items, q, slaFilter, statusFilter, assignee, sort, TERMINAL_STATUSES]);

    /* iter100.251 — Unique assignees from the loaded lead set (small
       list, computed on the fly). Keeps the dropdown context-aware:
       admins only see assignees who actually own leads in the current
       tab/kind. */
    const assigneeOptions = useMemo(() => {
        const byId = new Map();
        for (const r of items) {
            if (r.assignee_id && !byId.has(r.assignee_id)) {
                byId.set(r.assignee_id, r.assignee_name || r.assignee_id);
            }
        }
        return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [items]);

    return (
        <div className="space-y-6" data-testid="admin-leads-hub-page">
            {/* iter100.241 — Back-to-analytics breadcrumb (when arriving via drill-down). */}
            <BackToBreadcrumb />

            {/* iter100.249 — Slim header. iter100.251 — Adopted the
                overline + text-3xl pattern from AdminReferrals so both
                surfaces feel like the same product family.
                iter100.250 — Referrals quick-link kept for hoppability. */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Leads</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5" data-testid="leads-hub-title">Lead Command Center</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Assign, track, monitor SLAs, and generate reports across every lead source in one workspace.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <Link to="/admin/referrals">
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="leads-hub-referrals-link">
                            <Gift size={14} weight="bold" className="mr-1.5" />
                            Referrals
                        </Button>
                    </Link>
                    <Link to="/admin/leads/reports">
                        <Button size="sm" className="rounded-full" data-testid="leads-hub-reports-link">
                            <ChartLine size={14} weight="bold" className="mr-1.5" />
                            Reports
                        </Button>
                    </Link>
                </div>
            </div>

            {/* iter100.249 — Compact 6-tile KPI strip in Patients grammar. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="leads-hub-kpi-strip">
                    {["web", "b2b", "application"].map((k) => (
                        <KindCard
                            key={k}
                            kind={k}
                            data={stats?.by_kind?.[k]}
                            active={tab === k}
                            onClick={() => { setTab(k); setSlaFilter("all"); }}
                        />
                    ))}
                    <CrossTile
                        label="All open"
                        value={stats?.open_total ?? 0}
                        sub={`${stats?.closed_total ?? 0} closed`}
                        tone="primary"
                        Icon={UserList}
                        onClick={() => { setTab("all"); setSlaFilter("all"); setStatusFilter("open"); }}
                        testid="cross-tile-all-open"
                    />
                    <CrossTile
                        label="Unassigned"
                        value={stats?.unassigned ?? 0}
                        sub="Need an owner"
                        tone={stats?.unassigned ? "amber" : "muted"}
                        Icon={Clock}
                        onClick={() => { setTab("all"); setSlaFilter("not_assigned"); setStatusFilter("open"); }}
                        testid="cross-tile-unassigned"
                    />
                    <CrossTile
                        label="SLA breached"
                        value={stats?.sla_breached ?? 0}
                        sub={stats?.sla_at_risk != null ? `${stats.sla_at_risk} at risk` : ""}
                        tone={stats?.sla_breached ? "rose" : "muted"}
                        Icon={Flame}
                        onClick={() => { setTab("all"); setSlaFilter("breached"); setStatusFilter("open"); }}
                        testid="cross-tile-breached"
                    />
            </div>

            {/* Tabs — unified pill/segmented grammar (iter100.252). */}
            <AdminSegmentedTabs
                tabs={TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.Icon,
                    count: t.id === "referrals"
                        ? null
                        : t.id === "all"
                            ? (stats?.open_total ?? 0)
                            : (stats?.by_kind?.[t.id]?.open ?? 0),
                }))}
                active={tab}
                onChange={(id) => { setTab(id); setSlaFilter("all"); }}
                testid="leads-hub-tabs"
                tabTestidBase="leads-hub-tab"
                showZero
            />

            {/* iter100.250 — Referrals branch removed; that tab now
                lives at /admin/referrals.
                iter100.251 — Search bar right-slot now carries the
                same Sort + Filter + List/Grid controls as the doctor's
                My Patients page. Consistent shortcut vocabulary
                across the whole product. */}
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search name, email, phone, clinic, city…"
                searchTestid="leads-hub-search"
                testid="leads-hub-search-bar"
                rightSlot={
                    <>
                        <div className="relative">
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                data-testid="leads-hub-sort-select"
                                aria-label="Sort leads"
                            >
                                <option value="">Newest first</option>
                                <option value="oldest">Oldest first</option>
                                <option value="breached">SLA breached first</option>
                                <option value="name">Name A–Z</option>
                            </select>
                            <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                        </div>
                        <div className="relative">
                            <select
                                value={assignee}
                                onChange={(e) => setAssignee(e.target.value)}
                                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                data-testid="leads-hub-assignee-filter"
                                aria-label="Filter by assignee"
                            >
                                <option value="all">All assignees</option>
                                <option value="unassigned">Unassigned</option>
                                {assigneeOptions.map(([id, name]) => (
                                    <option key={id} value={id}>{name}</option>
                                ))}
                            </select>
                            <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                        </div>
                        <div className="inline-flex items-center rounded-md border border-border bg-card overflow-hidden h-9">
                            <button
                                type="button" onClick={() => setViewPersisted("list")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="leads-hub-view-list"
                                aria-pressed={view === "list"}
                                title="List view"
                            >
                                <ListBullets size={13} weight="bold" />
                            </button>
                            <button
                                type="button" onClick={() => setViewPersisted("grid")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="leads-hub-view-grid"
                                aria-pressed={view === "grid"}
                                title="Grid view"
                            >
                                <GridFour size={13} weight="bold" />
                            </button>
                        </div>
                        {(q || slaFilter !== "all" || statusFilter !== "open" || sort || assignee !== "all") && (
                            <button
                                type="button"
                                onClick={() => { setQ(""); setSlaFilter("all"); setStatusFilter("open"); setSort(""); setAssignee("all"); }}
                                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 h-8 px-3 rounded-full hover:bg-secondary/60"
                                data-testid="leads-hub-clear-filters"
                            >
                                <X size={10} /> Reset
                            </button>
                        )}
                    </>
                }
            />

            <div className="flex flex-col gap-2">
                <FilterChipRow
                    chips={[
                        { id: "open",     label: "Open",   tone: "primary", count: stats?.open_total ?? 0 },
                        { id: "terminal", label: "Closed", tone: "slate",   count: stats?.closed_total ?? 0 },
                        { id: "all",      label: "All",    tone: "primary" },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    testid="leads-hub-status-chips"
                    chipTestidPrefix="leads-hub-status-chip"
                />
                <FilterChipRow
                    chips={[
                        { id: "all",          label: "Any SLA",    tone: "primary" },
                        { id: "breached",     label: "Breached",   tone: "rose",    count: stats?.sla_breached ?? undefined },
                        { id: "at_risk",      label: "At risk",    tone: "amber",   count: stats?.sla_at_risk ?? undefined },
                        { id: "within",       label: "On track",   tone: "emerald" },
                        { id: "met",          label: "Met",        tone: "emerald" },
                        { id: "not_assigned", label: "Unassigned", tone: "slate",   count: stats?.unassigned ?? undefined },
                    ]}
                    value={slaFilter}
                    onChange={setSlaFilter}
                    testid="leads-hub-sla-chips"
                    chipTestidPrefix="leads-hub-sla-chip"
                />
            </div>

            {/* Body */}
            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10">Loading leads…</div>
            ) : filtered.length === 0 ? (
                <EmptyState onClear={() => { setQ(""); setSlaFilter("all"); setStatusFilter("open"); setSort(""); setAssignee("all"); }} />
            ) : view === "grid" ? (
                <LeadsGrid rows={filtered} />
            ) : (
                <LeadsList rows={filtered} />
            )}
        </div>
    );
}

/* ---------- Kind card (Web / B2B / Application) — large clickable card with SLA mini-bar ---------- */
const KIND_CARD_META = {
    web: {
        label: "Web leads",
        sub: "Smile quiz · Callback · Contact",
        Icon: Globe,
        active: "border-sky-300 ring-2 ring-sky-200 bg-sky-50/50",
        accent: "bg-sky-100 text-sky-700",
    },
    b2b: {
        label: "B2B leads",
        sub: "Provider sales pipeline",
        Icon: Handshake,
        active: "border-violet-300 ring-2 ring-violet-200 bg-violet-50/50",
        accent: "bg-violet-100 text-violet-700",
    },
    application: {
        label: "Applications",
        sub: "Doctor onboarding",
        Icon: Briefcase,
        active: "border-amber-300 ring-2 ring-amber-200 bg-amber-50/50",
        accent: "bg-amber-100 text-amber-700",
    },
};

function KindCard({ kind, data, active, onClick }) {
    const meta = KIND_CARD_META[kind];
    const open = data?.open ?? 0;
    const unassigned = data?.unassigned ?? 0;
    const sla = data?.sla || {};
    const slaCounts = {
        breached: sla.breached || 0,
        at_risk: sla.at_risk || 0,
        within: sla.within || 0,
        not_assigned: sla.not_assigned || 0,
    };
    const totalBar = Math.max(1, slaCounts.breached + slaCounts.at_risk + slaCounts.within + slaCounts.not_assigned);
    return (
        <button
            type="button"
            onClick={onClick}
            className={`relative text-left rounded-xl bg-card border p-3 transition-all hover:shadow-sm hover:-translate-y-px ${active ? meta.active : "border-border hover:border-primary/40"}`}
            data-testid={`leads-hub-kind-card-${kind}`}
            title={`${meta.label}: ${open} open · ${unassigned} unassigned · ${slaCounts.breached} breached`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <div className={`w-7 h-7 rounded-lg ${meta.accent} grid place-items-center`}>
                    <meta.Icon size={13} weight="fill" />
                </div>
                {unassigned > 0 && (
                    <span className="text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5">
                        {unassigned}
                    </span>
                )}
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none" data-testid={`leads-hub-kind-${kind}-open`}>
                {open}
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground mt-1 truncate">
                {meta.label}
            </div>
            <div className="h-1 rounded-full bg-secondary overflow-hidden flex mt-2" role="img" aria-label={`SLA: ${slaCounts.breached} breached, ${slaCounts.at_risk} at-risk, ${slaCounts.within} on track`}>
                <div className="h-full bg-rose-500" style={{ width: `${(slaCounts.breached / totalBar) * 100}%` }} />
                <div className="h-full bg-amber-500" style={{ width: `${(slaCounts.at_risk / totalBar) * 100}%` }} />
                <div className="h-full bg-emerald-500" style={{ width: `${(slaCounts.within / totalBar) * 100}%` }} />
                <div className="h-full bg-slate-300" style={{ width: `${(slaCounts.not_assigned / totalBar) * 100}%` }} />
            </div>
        </button>
    );
}

/* ---------- Cross-cutting tile (same footprint as KindCard) ---------- */
function CrossTile({ label, value, sub, tone, Icon, onClick, testid }) {
    const toneCls = tone === "rose"
        ? "border-rose-200 bg-rose-50/50 text-rose-700"
        : tone === "amber"
        ? "border-amber-200 bg-amber-50/50 text-amber-700"
        : tone === "primary"
        ? "border-primary/30 bg-primary/5 text-primary"
        : "border-border bg-card text-muted-foreground";
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-xl border p-3 transition hover:shadow-sm hover:-translate-y-px ${toneCls}`}
            data-testid={testid}
            title={`${label}: ${value}${sub ? ` · ${sub}` : ""}`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <Icon size={14} weight="duotone" className="opacity-80" />
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1 truncate">{label}</div>
            {sub && <div className="text-[10px] opacity-70 mt-0.5 truncate">{sub}</div>}
        </button>
    );
}

/* ---------- Empty state ---------- */
function EmptyState({ onClear }) {
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="leads-hub-empty">
            <MagnifyingGlass size={28} className="mx-auto text-muted-foreground mb-3" />
            <div className="font-display text-lg font-bold">No leads match your filters</div>
            <p className="text-xs text-muted-foreground mt-1.5">Adjust the SLA or search filters to see more.</p>
            <Button onClick={onClear} variant="outline" className="rounded-full mt-4">Clear filters</Button>
        </div>
    );
}

/* ---------- List rows ---------- */
function LeadsList({ rows }) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="leads-hub-list">
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                <div className="col-span-3">Lead</div>
                <div className="col-span-2">Kind</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Assignee</div>
                <div className="col-span-2">SLA</div>
                <div className="col-span-1 text-right">Action</div>
            </div>
            {rows.map((r) => {
                const kp = KIND_PILLS[r.kind] || KIND_PILLS.web;
                return (
                    <Link
                        key={`${r.kind}-${r.id}`}
                        to={`/admin/leads/${r.kind}/${r.id}`}
                        className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 hover:bg-secondary/30 transition items-center"
                        data-testid={`leads-hub-row-${r.kind}-${r.id}`}
                    >
                        <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary font-semibold grid place-items-center shrink-0">
                                {initials(r.title)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold truncate" data-testid={`leads-hub-row-title-${r.id}`}>{r.title || "—"}</div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                    {r.subtitle || r.email || r.phone || "—"}
                                </div>
                            </div>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-1.5 min-w-0">
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold inline-flex items-center gap-1 ${kp.cls}`}>
                                <kp.Icon size={10} weight="fill" /> {kp.label}
                            </span>
                            {r.score != null && r.kind === "web" && (
                                <span className="text-[10px] text-muted-foreground">· score {r.score}</span>
                            )}
                        </div>
                        <div className="md:col-span-2 text-xs min-w-0">
                            <span className="font-mono uppercase tracking-wide text-[11px]">{(r.status || "").replace(/_/g, " ")}</span>
                        </div>
                        <div className="md:col-span-2 text-xs min-w-0">
                            {r.assignee_id ? (
                                <div className="truncate">
                                    <span className="font-medium">{r.assignee_name}</span>
                                    {r.assignee_role && <span className="text-muted-foreground"> · {r.assignee_role}</span>}
                                </div>
                            ) : (
                                <span className="text-amber-700 font-medium">Unassigned</span>
                            )}
                        </div>
                        <div className="md:col-span-2 min-w-0">
                            <SLAPill sla={r.sla} kind={r.kind} testid={`leads-hub-sla-${r.id}`} />
                        </div>
                        <div className="md:col-span-1 flex md:justify-end">
                            <ArrowRight size={14} className="text-muted-foreground" />
                        </div>
                    </Link>
                );
            })}
        </div>
    );
}

/* iter100.251 — Grid variant. Same data as LeadsList but rendered as
   compact cards so admins triaging on large screens can see many more
   leads at a glance. Uses the same tone/SLA/kind vocabulary. */
function LeadsGrid({ rows }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5" data-testid="leads-hub-grid">
            {rows.map((r) => {
                const kp = KIND_PILLS[r.kind] || KIND_PILLS.web;
                return (
                    <Link
                        key={`${r.kind}-${r.id}`}
                        to={`/admin/leads/${r.kind}/${r.id}`}
                        className="p-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all block"
                        data-testid={`leads-hub-card-${r.kind}-${r.id}`}
                    >
                        <div className="flex items-start gap-2.5">
                            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary font-semibold grid place-items-center shrink-0 text-xs">
                                {initials(r.title)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-sm truncate" data-testid={`leads-hub-card-title-${r.id}`}>{r.title || "—"}</div>
                                <div className="text-[10.5px] text-muted-foreground truncate">
                                    {r.subtitle || r.email || r.phone || "—"}
                                </div>
                            </div>
                        </div>
                        <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold inline-flex items-center gap-1 ${kp.cls}`}>
                                <kp.Icon size={10} weight="fill" /> {kp.label}
                            </span>
                            <SLAPill sla={r.sla} kind={r.kind} testid={`leads-hub-card-sla-${r.id}`} />
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                            <div className="text-[10.5px] text-muted-foreground truncate flex-1">
                                {r.assignee_id ? (
                                    <>
                                        <span className="font-medium">{r.assignee_name}</span>
                                    </>
                                ) : (
                                    <span className="text-amber-700 font-medium">Unassigned</span>
                                )}
                            </div>
                            <span className="text-[10.5px] font-mono uppercase tracking-wide text-muted-foreground/80 truncate">
                                {(r.status || "").replace(/_/g, " ")}
                            </span>
                        </div>
                    </Link>
                );
            })}
        </div>
    );
}
